import React, { useState, useEffect } from 'react';
import {
  MessageSquare,
  Phone,
  Video,
  Search,
  Send,
  Paperclip,
  MoreVertical,
  Mic,
  MicOff,
  Volume2,
  VolumeX,
  PhoneCall,
  Loader2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { extensionsAPI, dashboardAPI } from '../services/api';

const VoiceChat = () => {
  const [extensions, setExtensions] = useState([]);
  const [activeCalls, setActiveCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContact, setSelectedContact] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [isMuted, setIsMuted] = useState(false);
  const [isSpeakerOn, setIsSpeakerOn] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [extsRes, callsRes] = await Promise.all([
        extensionsAPI.list(),
        dashboardAPI.getActiveCalls()
      ]);
      setExtensions(extsRes.data);
      setActiveCalls(callsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredContacts = extensions.filter(
    (ext) =>
      ext.type === 'user' &&
      (ext.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        ext.id.includes(searchQuery))
  );

  const getStatusColor = (status) => {
    const colors = {
      online: 'bg-green-500',
      busy: 'bg-red-500',
      away: 'bg-yellow-500',
      dnd: 'bg-purple-500',
      offline: 'bg-gray-500'
    };
    return colors[status] || 'bg-gray-500';
  };

  const handleSendMessage = () => {
    if (!message.trim() || !selectedContact) return;

    const newMessage = {
      id: Date.now(),
      text: message,
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      isMe: true
    };

    setMessages([...messages, newMessage]);
    setMessage('');

    // Simulate response
    setTimeout(() => {
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now() + 1,
          text: "Thanks for your message! I'll get back to you shortly.",
          time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          isMe: false
        }
      ]);
    }, 1500);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Voice & Chat</h1>
        <p className="text-gray-400 mt-1">Communicate with team members</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-[calc(100vh-250px)]">
        {/* Contacts List */}
        <Card className="bg-[#12121a] border-[#1e1e2d] flex flex-col">
          <CardHeader className="border-b border-[#1e1e2d] pb-4">
            <div className="relative">
              <Search className="w-5 h-5 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                placeholder="Search contacts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-[#1e1e2d] border-[#2a2a3d] text-white placeholder-gray-500"
              />
            </div>
          </CardHeader>
          <CardContent className="flex-1 overflow-hidden p-0">
            <ScrollArea className="h-full">
              <div className="divide-y divide-[#1e1e2d]">
                {filteredContacts.map((contact) => (
                  <div
                    key={contact.id}
                    onClick={() => {
                      setSelectedContact(contact);
                      setMessages([]);
                    }}
                    className={`p-4 cursor-pointer hover:bg-[#1e1e2d] transition-colors ${
                      selectedContact?.id === contact.id ? 'bg-[#1e1e2d]' : ''
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="relative">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                          <span className="text-white font-semibold text-sm">
                            {contact.name
                              .split(' ')
                              .map((n) => n[0])
                              .join('')}
                          </span>
                        </div>
                        <div
                          className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-[#12121a] ${getStatusColor(
                            contact.status
                          )}`}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium truncate">{contact.name}</p>
                        <p className="text-sm text-gray-500 truncate">Ext: {contact.id}</p>
                      </div>
                      <Badge
                        variant="outline"
                        className={`text-xs ${
                          contact.status === 'online'
                            ? 'border-green-500 text-green-400'
                            : contact.status === 'busy'
                            ? 'border-red-500 text-red-400'
                            : 'border-gray-500 text-gray-400'
                        }`}
                      >
                        {contact.status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Chat/Call Area */}
        <Card className="bg-[#12121a] border-[#1e1e2d] lg:col-span-2 flex flex-col">
          {selectedContact ? (
            <Tabs defaultValue="chat" className="flex-1 flex flex-col">
              <CardHeader className="border-b border-[#1e1e2d] pb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                      <span className="text-white font-semibold">
                        {selectedContact.name
                          .split(' ')
                          .map((n) => n[0])
                          .join('')}
                      </span>
                    </div>
                    <div>
                      <p className="text-white font-medium">{selectedContact.name}</p>
                      <p className="text-sm text-gray-500">Extension: {selectedContact.id}</p>
                    </div>
                  </div>
                  <TabsList className="bg-[#1e1e2d]">
                    <TabsTrigger value="chat" className="data-[state=active]:bg-blue-600">
                      <MessageSquare className="w-4 h-4" />
                    </TabsTrigger>
                    <TabsTrigger value="call" className="data-[state=active]:bg-green-600">
                      <Phone className="w-4 h-4" />
                    </TabsTrigger>
                  </TabsList>
                </div>
              </CardHeader>

              {/* Chat Tab */}
              <TabsContent value="chat" className="flex-1 flex flex-col m-0">
                <ScrollArea className="flex-1 p-4">
                  <div className="space-y-4">
                    {messages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`flex ${msg.isMe ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[70%] p-3 rounded-lg ${
                            msg.isMe
                              ? 'bg-blue-600 text-white'
                              : 'bg-[#1e1e2d] text-white'
                          }`}
                        >
                          <p className="text-sm">{msg.text}</p>
                          <p className={`text-xs mt-1 ${msg.isMe ? 'text-blue-200' : 'text-gray-500'}`}>
                            {msg.time}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
                <div className="p-4 border-t border-[#1e1e2d]">
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="icon" className="text-gray-400 hover:text-white">
                      <Paperclip className="w-5 h-5" />
                    </Button>
                    <Input
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                      placeholder="Type a message..."
                      className="flex-1 bg-[#1e1e2d] border-[#2a2a3d] text-white"
                    />
                    <Button
                      onClick={handleSendMessage}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      <Send className="w-5 h-5" />
                    </Button>
                  </div>
                </div>
              </TabsContent>

              {/* Call Tab */}
              <TabsContent value="call" className="flex-1 flex flex-col items-center justify-center m-0 p-8">
                <div className="text-center">
                  <div className="w-24 h-24 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center mx-auto mb-6">
                    <span className="text-white font-bold text-3xl">
                      {selectedContact.name.split(' ').map((n) => n[0]).join('')}
                    </span>
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-1">{selectedContact.name}</h3>
                  <p className="text-gray-400 mb-8">Extension: {selectedContact.id}</p>

                  <div className="flex items-center justify-center gap-4 mb-8">
                    <Button
                      variant="outline"
                      size="lg"
                      onClick={() => setIsMuted(!isMuted)}
                      className={`rounded-full w-14 h-14 ${isMuted ? 'bg-red-500/20 border-red-500 text-red-400' : 'border-[#2a2a3d] text-gray-400'}`}
                    >
                      {isMuted ? <MicOff className="w-6 h-6" /> : <Mic className="w-6 h-6" />}
                    </Button>
                    <Button
                      size="lg"
                      className="rounded-full w-16 h-16 bg-green-600 hover:bg-green-700"
                    >
                      <Phone className="w-7 h-7" />
                    </Button>
                    <Button
                      variant="outline"
                      size="lg"
                      onClick={() => setIsSpeakerOn(!isSpeakerOn)}
                      className={`rounded-full w-14 h-14 ${!isSpeakerOn ? 'bg-red-500/20 border-red-500 text-red-400' : 'border-[#2a2a3d] text-gray-400'}`}
                    >
                      {isSpeakerOn ? <Volume2 className="w-6 h-6" /> : <VolumeX className="w-6 h-6" />}
                    </Button>
                  </div>

                  <p className="text-sm text-gray-500">
                    Click the green button to initiate a call
                  </p>
                </div>
              </TabsContent>
            </Tabs>
          ) : (
            <div className="h-full flex items-center justify-center">
              <div className="text-center">
                <div className="w-16 h-16 bg-[#1e1e2d] rounded-full flex items-center justify-center mx-auto mb-4">
                  <MessageSquare className="w-8 h-8 text-gray-500" />
                </div>
                <h3 className="text-lg font-medium text-white mb-2">Select a Contact</h3>
                <p className="text-gray-400">Choose a contact from the list to start chatting or calling</p>
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* Active Calls */}
      {activeCalls.length > 0 && (
        <Card className="bg-[#12121a] border-[#1e1e2d]">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <PhoneCall className="w-5 h-5 text-green-400" />
              Active Calls
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {activeCalls.map((call) => (
                <div key={call.id} className="p-4 bg-[#1e1e2d] rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <Badge className="bg-green-500/20 text-green-400">{call.status}</Badge>
                    <span className="text-white font-mono">{call.duration}</span>
                  </div>
                  <p className="text-white font-medium">{call.from_name}</p>
                  <p className="text-sm text-gray-400">→ {call.to_name}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default VoiceChat;
